import 'react-native-get-random-values'

import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { WebView } from 'react-native-webview'

import { useLocalSearchParams, useRouter } from 'expo-router'

import { useStore } from '../../models'
import { color } from '../../theme'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import BackButtonIcon from './assets/Arrow-left.svg'
import { NextModuleScreen } from './NextModuleScreen'

import { calculateZogoInsets, getHTML, ZogoConfig } from './zogo-screen.utils'

const logger = extendLogger(LoggerContexts.ZogoScreen)

type ZogoMessage = {
  type: 'finishModule' | 'startModule'
  startModuleTime?: Date
  endModuleTime?: Date
}

export const ZogoScreen = observer(() => {
  const router = useRouter()
  const params = useLocalSearchParams<{ moduleId: string }>()
  const moduleId = Number(params.moduleId)

  const insets = useSafeAreaInsets()
  const store = useStore()
  const zogoCredential = store.zogo.zogoCredential
  const iframeViewed = store.zogo.iframeViewed
  const saveLearningModuleStats = store.zogo.saveLearningModuleStats

  const ZOGO_PROD_URL = process.env.EXPO_PUBLIC_ZOGO_PROD_URL || ''
  const ZOGO_STAGE = process.env.EXPO_PUBLIC_ZOGO_STAGE || 'dev'

  const [zogoModuleId, setZogoModuleId] = React.useState(moduleId)
  const [isCurrentModuleCompleted, setIsCurrentModuleCompleted] = React.useState<boolean>(false)
  const zogoInsets = calculateZogoInsets(insets.top, insets.bottom)
  const zogoProdConfig: ZogoConfig = {
    moduleId: zogoModuleId,
    zogoToken: zogoCredential?.access_token,
    baseUrl: ZOGO_PROD_URL
  }
  const zogoHtml = () => {
    logger.info('zogoHtml zogoProdConfig', zogoProdConfig)
    return getHTML(zogoProdConfig, zogoInsets)
  }

  useEffect(() => {
    if (iframeViewed) {
      saveLearningModuleStats(moduleId)
    }
  }, [saveLearningModuleStats, moduleId, iframeViewed])

  const onBackZogo = () => {
    router.replace('/(root)/(tabs)/learning')
  }

  const handleZogoMessages = async (data: string) => {
    const message = JSON.parse(data ?? {}) as ZogoMessage

    if (message.type === 'startModule') {
      store.zogo.setIframeViewed()
      const { startModuleTime } = message
      // TODO: save when the user started the module and zogo is ready, this can be used
      // to track users who did not complete the module or partially completed it
      logger.debug('handleZogoMessages startModule', { startModuleTime })
    } else if (message.type === 'finishModule') {
      const saved = ZOGO_STAGE === 'prod' ? saveLearningModuleStats(moduleId) : true

      if (saved) {
        const { startModuleTime, endModuleTime } = message
        logger.debug('handleZogoMessages finishModule', { startModuleTime, endModuleTime })
      }
      if (store.session.selectedDailyTask) {
        store.session.updateDailyTaskStatus()
      }
      setIsCurrentModuleCompleted(true)

      router.replace('/(root)/(tabs)/learning')
    }
  }

  return (
    <View style={styles.container}>
      {isCurrentModuleCompleted ? (
        <NextModuleScreen
          resetCurrentModule={() => setIsCurrentModuleCompleted(false)}
          setZogoModuleId={setZogoModuleId}
          onClose={onBackZogo}
          currentModuleId={zogoModuleId}
        />
      ) : (
        <React.Fragment>
          <View
            style={[
              styles.header,
              {
                paddingTop: insets.top
              }
            ]}
          >
            <TouchableOpacity onPress={onBackZogo}>
              <BackButtonIcon />
            </TouchableOpacity>
          </View>
          <WebView
            originWhitelist={['*']}
            androidLayerType="software"
            source={{
              html: zogoHtml()
            }}
            style={{
              flex: 1,
              backgroundColor: color.palette.cream
            }}
            javaScriptCanOpenWindowsAutomatically={false}
            onMessage={(e) => handleZogoMessages(e.nativeEvent.data)}
          />
        </React.Fragment>
      )}
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.palette.cream
  },
  header: {
    paddingLeft: 8,
    paddingBottom: 8,
    backgroundColor: color.palette.cream
  },
  spinnerContainer: {
    flex: 1
  }
})
